import { useState } from 'react';
import { ActivityIndicator, StyleSheet } from 'react-native';
import { Box, HStack, Text, VStack } from '../../../components/primitives';
import { Button } from '../../../components/primitives/Button';
import { FormInput } from '../../../components/primitives/FormInput';
import { Sheet } from '../../../components/primitives/Sheet';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import { fieldPlaceholderColor } from '../../../theme/typography';
import { AudienceType, type CampaignChannel, type CreateCampaignInput } from '../types';
import { ChannelSelector } from './ChannelSelector';

interface CreateCampaignSheetProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (input: CreateCampaignInput) => void;
  organizationId: string;
  isLoading?: boolean;
}

export function CreateCampaignSheet({
  visible,
  onClose,
  onSubmit,
  organizationId,
  isLoading,
}: Readonly<CreateCampaignSheetProps>) {
  const { t } = useTranslation();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [channels, setChannels] = useState<CampaignChannel[]>([]);
  const [audienceType, setAudienceType] = useState<AudienceType>(AudienceType.MANUAL);

  const isValid = name.trim().length >= 3 && channels.length > 0;

  const handleSubmit = () => {
    if (!isValid || isLoading) return;

    onSubmit({
      organizationId,
      name: name.trim(),
      description: description.trim() || undefined,
      channels,
      audienceType,
    });

    setName('');
    setDescription('');
    setChannels([]);
    setAudienceType(AudienceType.MANUAL);
  };

  return (
    <Sheet visible={visible} onClose={onClose} title={t('campaigns.createNew')} size="md">
      <VStack spacing="lg" style={styles.content}>
        <VStack spacing="xs">
          <Text variant="caption" color={colors.text.secondary} style={styles.label}>
            {t('campaigns.form.name')}
          </Text>
          <FormInput
            value={name}
            onChangeText={setName}
            placeholder={t('campaigns.form.namePlaceholder')}
            placeholderTextColor={fieldPlaceholderColor}
            style={styles.input}
            maxLength={200}
          />
        </VStack>

        <VStack spacing="xs">
          <Text variant="caption" color={colors.text.secondary} style={styles.label}>
            {t('campaigns.form.description')}
          </Text>
          <FormInput
            value={description}
            onChangeText={setDescription}
            placeholder={t('campaigns.form.descriptionPlaceholder')}
            placeholderTextColor={fieldPlaceholderColor}
            style={[styles.input, styles.textArea]}
            multiline
            numberOfLines={3}
            maxLength={1000}
          />
        </VStack>

        <VStack spacing="xs">
          <Text variant="caption" color={colors.text.secondary} style={styles.label}>
            {t('campaigns.form.channels')}
          </Text>
          <Text variant="caption" color={colors.text.disabled}>
            {t('campaigns.form.channelsHint')}
          </Text>
          <ChannelSelector selected={channels} onChange={setChannels} />
        </VStack>

        <VStack spacing="xs">
          <Text variant="caption" color={colors.text.secondary} style={styles.label}>
            {t('campaigns.form.audienceType')}
          </Text>
          <HStack spacing="sm">
            <Box
              pressable
              onPress={() => setAudienceType(AudienceType.MANUAL)}
              borderRadius="md"
              padding="sm"
              backgroundColor={
                audienceType === AudienceType.MANUAL ? colors.primary.muted : 'transparent'
              }
              style={[
                styles.audienceChip,
                audienceType === AudienceType.MANUAL && styles.audienceChipSelected,
              ]}
            >
              <Text
                variant="caption"
                color={
                  audienceType === AudienceType.MANUAL ? colors.primary[400] : colors.text.tertiary
                }
                style={styles.audienceLabel}
              >
                {t('campaigns.form.manual')}
              </Text>
            </Box>
            <Box
              pressable
              onPress={() => setAudienceType(AudienceType.SEGMENT)}
              borderRadius="md"
              padding="sm"
              backgroundColor={
                audienceType === AudienceType.SEGMENT ? colors.primary.muted : 'transparent'
              }
              style={[
                styles.audienceChip,
                audienceType === AudienceType.SEGMENT && styles.audienceChipSelected,
              ]}
            >
              <Text
                variant="caption"
                color={
                  audienceType === AudienceType.SEGMENT ? colors.primary[400] : colors.text.tertiary
                }
                style={styles.audienceLabel}
              >
                {t('campaigns.form.segment')}
              </Text>
            </Box>
          </HStack>
        </VStack>

        <Button onPress={handleSubmit} disabled={!isValid || isLoading} variant="primary" fullWidth>
          {isLoading ? (
            <ActivityIndicator size="small" color={colors.background.primary} />
          ) : (
            t('campaigns.form.create')
          )}
        </Button>
      </VStack>
    </Sheet>
  );
}

const styles = StyleSheet.create({
  content: {
    paddingTop: tokens.spacing.sm,
  },
  label: {
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  input: {
    backgroundColor: colors.background.tertiary,
    borderRadius: tokens.radius.smPlus,
    borderWidth: 1,
    borderColor: colors.border.default,
    paddingHorizontal: tokens.spacing.md,
    paddingVertical: tokens.spacing.smPlus,
    color: colors.text.primary,
    fontSize: tokens.fontSize.base,
  },
  textArea: {
    minHeight: 72,
    textAlignVertical: 'top',
  },
  audienceChip: {
    borderWidth: 1,
    borderColor: colors.border.default,
  },
  audienceChipSelected: {
    borderColor: colors.primary.subtle,
  },
  audienceLabel: {
    fontWeight: '600',
  },
});
